import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl, getApiUrl, getApiHeaders } from '../utils/apiConfig';
import {
  CONNECTION_STATE,
  ERROR_KIND,
  classifyRequestError,
  diagnoseApiReachability,
} from '../utils/connectionDiagnostics';
import { setTrackedProStatus } from '../utils/eventTracker';

// 구독 컨텍스트 생성
const SubscriptionContext = createContext();

// 구독 컨텍스트를 사용하는 커스텀 훅
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// 구독 플랜 정보
export const SUBSCRIPTION_PLANS = {
  free: {
    name: "FREE",
    price: 0,
    subtitle: "교육 콘텐츠 제작을 시작하는 모든 선생님을 위한 기본 플랜",
    features: [
      "**토큰 제한 있음** (시간당 2,000개 자동 충전)",
    ],
    limitations: [
      "토큰 충전 대기 시간 있음",
      "연속 작업 시 제한 가능"
    ]
  },
  pro: {
    name: "PRO",
    price: 60000, // 3개월 플랜 기준
    subtitle: "제한 없이 자유롭게, 무제한 생성을 위한 기간제 플랜",
    features: [
      "**토큰 제한 없음** (무제한 활용 가능)"
    ],
    limitations: []
  }
};

// localStorage 구독 캐시 헬퍼
const CACHE_KEY_PREFIX = 'wq_sub_';
const VERIFICATION_RETRY_INTERVAL = 45 * 1000;
const SUBSCRIPTION_REQUEST_TIMEOUT = 8 * 1000;
const CONNECTION_WATCHDOG_DELAY = 3 * 1000;
export const FORCE_REFRESH_WINDOW_MS = 30 * 1000;
export const FORCE_REFRESH_MAX_PER_WINDOW = 15;

export const VERIFICATION_STATE = Object.freeze({
  CHECKING: 'checking',
  FAILED: 'failed',
  IDLE: 'idle',
  VERIFIED: 'verified',
});

function saveSubscriptionCache(uid, data) {
  try {
    const cacheEntry = {
      data,
      uid,
      savedAt: Date.now(),
    };
    localStorage.setItem(`${CACHE_KEY_PREFIX}${uid}`, JSON.stringify(cacheEntry));
  } catch (_e) { /* localStorage 사용 불가 환경 무시 */ }
}

function loadSubscriptionCache(uid) {
  try {
    const raw = localStorage.getItem(`${CACHE_KEY_PREFIX}${uid}`);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (cached.uid !== uid) return null;

    // end_date가 있으면 만료 체크 (만료된 캐시는 무시)
    if (cached.data?.end_date) {
      const endDate = new Date(cached.data.end_date);
      if (endDate < new Date()) return null;
    }
    return cached.data;
  } catch (_e) { return null; }
}

function clearSubscriptionCache(uid) {
  try {
    localStorage.removeItem(`${CACHE_KEY_PREFIX}${uid}`);
  } catch (_e) { /* 무시 */ }
}

export const SubscriptionProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [storedSubscriptionInfo, setSubscriptionInfo] = useState(null);
  // 계정을 바로 바꾸면 새 조회가 끝나기 전까지 이전 계정 상태가 남는다. 현재 계정 것만 내보낸다.
  const subscriptionInfo = currentUser && storedSubscriptionInfo?.owner_uid === currentUser.uid
    ? storedSubscriptionInfo
    : null;
  const [plans, setPlans] = useState(SUBSCRIPTION_PLANS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verificationState, setVerificationState] = useState(VERIFICATION_STATE.IDLE);
  const [verificationFailureReason, setVerificationFailureReason] = useState(null);
  const [connectionState, setConnectionState] = useState(CONNECTION_STATE.UNKNOWN);
  const [connectionEvidence, setConnectionEvidence] = useState(null);
  const [lastVerifiedAt, setLastVerifiedAt] = useState(null);

  // useRef로 캐시 체크용 참조 (useCallback 의존성 순환 방지)
  const lastFetchTimeRef = useRef(null);
  const subscriptionInfoRef = useRef(null);
  const inflightRef = useRef(null);
  const forceRefreshTimesRef = useRef([]);
  const stormReportedRef = useRef(false);
  const connectionDiagnosticAttemptedRef = useRef(false);
  const connectionDiagnosticPromiseRef = useRef(null);
  const connectionDiagnosticGenerationRef = useRef(0);

  // subscriptionInfo state 변경 시 ref 동기화
  useEffect(() => {
    subscriptionInfoRef.current = subscriptionInfo;
  }, [subscriptionInfo]);

  const resetConnectionDiagnostics = useCallback((markHealthy = false) => {
    connectionDiagnosticGenerationRef.current += 1;
    connectionDiagnosticAttemptedRef.current = false;
    connectionDiagnosticPromiseRef.current = null;
    setConnectionEvidence(null);
    setConnectionState(markHealthy ? CONNECTION_STATE.HEALTHY : CONNECTION_STATE.UNKNOWN);
  }, []);

  const startConnectionDiagnostics = useCallback(() => {
    if (!currentUser || connectionDiagnosticAttemptedRef.current) {
      return connectionDiagnosticPromiseRef.current || Promise.resolve(null);
    }

    connectionDiagnosticAttemptedRef.current = true;
    const generation = connectionDiagnosticGenerationRef.current;
    const promise = diagnoseApiReachability({
      apiBaseUrl: getApiBaseUrl(),
    })
      .then((result) => {
        if (generation !== connectionDiagnosticGenerationRef.current) return null;
        setConnectionEvidence(result.evidence);
        setConnectionState(result.state);
        return result;
      })
      .catch(() => {
        if (generation === connectionDiagnosticGenerationRef.current) {
          setConnectionEvidence(null);
          setConnectionState(CONNECTION_STATE.UNKNOWN);
        }
        return null;
      })
      .finally(() => {
        if (generation === connectionDiagnosticGenerationRef.current) {
          connectionDiagnosticPromiseRef.current = null;
        }
      });

    connectionDiagnosticPromiseRef.current = promise;
    return promise;
  }, [currentUser]);

  // API 호출 헬퍼 함수
  const makeAuthenticatedRequest = useCallback(async (url, options = {}) => {
    if (!currentUser) {
      throw new Error('[AUTH_NO_USER] 로그인이 필요합니다.');
    }

    const {
      signal: externalSignal,
      timeoutMs = 0,
      ...requestOptions
    } = options;

    let token;
    try {
      token = await currentUser.getIdToken();
    } catch (tokenError) {
      // 캐시된 토큰 실패 → 강제 갱신 시도 (모바일 인앱브라우저 대응)
      console.warn(
        `⚠️ [AUTH_TOKEN_RETRY] getIdToken() 실패, getIdToken(true) 강제 갱신 시도 | uid=${currentUser.uid} | error=${tokenError.message}`
      );
      try {
        token = await currentUser.getIdToken(true);
        console.info(`✅ [AUTH_TOKEN_RETRY] 강제 갱신 성공 | uid=${currentUser.uid}`);
      } catch (forceRefreshError) {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const browserInfo = navigator.userAgent.slice(0, 120);
        console.error(
          `❌ [AUTH_TOKEN_FAIL] 강제 갱신도 실패 | mobile=${isMobile} | uid=${currentUser.uid} | email=${currentUser.email} | browser=${browserInfo} | error=${forceRefreshError.message}`
        );
        throw new Error(`[AUTH_TOKEN_FAIL] ${forceRefreshError.message}`);
      }
    }

    try {
      const fullUrl = getApiUrl(url);
      const requestWithToken = async (requestToken) => {
        const controller = timeoutMs > 0 ? new AbortController() : null;
        let didTimeout = false;
        let timeoutId = null;
        const forwardExternalAbort = () => controller?.abort();

        if (controller && externalSignal) {
          if (externalSignal.aborted) {
            controller.abort();
          } else {
            externalSignal.addEventListener('abort', forwardExternalAbort, { once: true });
          }
        }
        if (controller) {
          timeoutId = setTimeout(() => {
            didTimeout = true;
            controller.abort();
          }, timeoutMs);
        }

        try {
          return await fetch(fullUrl, {
            ...requestOptions,
            headers: {
              ...getApiHeaders(requestToken),
              ...requestOptions.headers,
            },
            signal: controller?.signal || externalSignal,
          });
        } catch (requestError) {
          if (didTimeout) {
            throw new Error(`[API_TIMEOUT] ${timeoutMs}ms 안에 응답이 없었습니다.`);
          }
          throw requestError;
        } finally {
          if (timeoutId) clearTimeout(timeoutId);
          externalSignal?.removeEventListener?.('abort', forwardExternalAbort);
        }
      };
      let response = await requestWithToken(token);

      // Firebase SDK 캐시는 정상이어도 서버가 만료·폐기·시계 오차로 401을 줄 수 있다.
      // 동일 토큰을 반복하지 않고 새 ID 토큰으로 딱 한 번만 재요청한다.
      if (response.status === 401) {
        console.warn(
          `⚠️ [AUTH_HTTP_401_REFRESH] 새 토큰으로 구독 API 재요청 | uid=${currentUser.uid}`
        );
        const refreshedToken = await currentUser.getIdToken(true);
        response = await requestWithToken(refreshedToken);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error(
          `❌ [API_HTTP_${response.status}] ${url} | uid=${currentUser.uid} | response=`,
          errorData
        );
        throw new Error(`[API_HTTP_${response.status}] ${errorData.error || 'Unknown error'}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      if (
        error.message.startsWith('[API_HTTP_')
        || error.message.startsWith('[API_TIMEOUT]')
        || error.message.startsWith('[NETWORK_FAIL]')
      ) {
        throw error;
      }

      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const errorKind = classifyRequestError(error);
      const errorTag = errorKind === ERROR_KIND.NETWORK ? 'NETWORK_FAIL' : 'API_UNKNOWN';
      console.error(
        `❌ [${errorTag}] ${url} | mobile=${isMobile} | uid=${currentUser.uid} | error=${error.message}`
      );
      throw new Error(`[${errorTag}] ${error.message}`);
    }
  }, [currentUser]);

  // 구독 상태 조회 (캐싱 적용 - 5분, Fail-Open 패턴)
  const fetchSubscriptionStatus = useCallback(async (forceRefresh = false) => {
    if (!currentUser) {
      setSubscriptionInfo(null);
      subscriptionInfoRef.current = null;
      setVerificationState(VERIFICATION_STATE.IDLE);
      setVerificationFailureReason(null);
      setLastVerifiedAt(null);
      resetConnectionDiagnostics(false);
      return null;
    }

    // 캐시 확인 (5분 이내 성공 데이터가 있으면 재조회 안함)
    const CACHE_DURATION = 5 * 60 * 1000;
    const now = Date.now();
    if (
      !forceRefresh &&
      lastFetchTimeRef.current &&
      subscriptionInfoRef.current?.owner_uid === currentUser.uid &&
      (now - lastFetchTimeRef.current < CACHE_DURATION)
    ) {
      return subscriptionInfoRef.current;
    }

    // 같은 사용자의 요청이 진행 중이면 새 네트워크 호출 없이 같은 Promise를 재사용한다.
    // (계정 전환 직후 이전 사용자의 응답을 새 사용자에게 돌려주지 않도록 uid를 함께 비교)
    if (inflightRef.current && inflightRef.current.uid === currentUser.uid) {
      return inflightRef.current.promise;
    }

    // forceRefresh 폭주 차단: 30초 창에서 임계치를 넘으면 서버를 부르지 않고 마지막 결과를 반환한다.
    if (forceRefresh) {
      const t = Date.now();
      forceRefreshTimesRef.current = forceRefreshTimesRef.current.filter(
        (ts) => t - ts < FORCE_REFRESH_WINDOW_MS
      );
      forceRefreshTimesRef.current.push(t);
      if (
        forceRefreshTimesRef.current.length > FORCE_REFRESH_MAX_PER_WINDOW
        && subscriptionInfoRef.current
      ) {
        const count = forceRefreshTimesRef.current.length;
        console.error(
          `🚨 [SUBSCRIPTION_REFRESH_STORM] forceRefresh 폭주 차단 | uid=${currentUser.uid} | count=${count} | window_ms=${FORCE_REFRESH_WINDOW_MS}`
        );
        if (!stormReportedRef.current) {
          stormReportedRef.current = true;
          try {
            fetch(getApiUrl('/api/log/client-error'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                event_type: 'SUBSCRIPTION_REFRESH_STORM',
                user_uid: currentUser.uid,
                user_email: currentUser.email || 'unknown',
                details: `forceRefresh ${count}회 / ${FORCE_REFRESH_WINDOW_MS}ms`,
                extra: {
                  count,
                  window_ms: FORCE_REFRESH_WINDOW_MS,
                  path: window.location.pathname,
                  user_agent: navigator.userAgent.slice(0, 200),
                },
              }),
              keepalive: true,
            }).catch(() => {});
          } catch (_e) { /* noop */ }
        }
        return subscriptionInfoRef.current;
      }
    }

    const runFetch = async () => {
      if (forceRefresh) {
        resetConnectionDiagnostics(false);
      }
      setLoading(true);
      setError(null);
      setVerificationState(VERIFICATION_STATE.CHECKING);

      const MAX_RETRIES = 3;
      const RETRY_DELAYS = [500, 1000, 2000];

      const errors = [];
      let lastFailureKind = ERROR_KIND.UNKNOWN;
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        const watchdogId = attempt === 0
          ? setTimeout(() => {
            startConnectionDiagnostics();
          }, CONNECTION_WATCHDOG_DELAY)
          : null;

        try {
          const data = await makeAuthenticatedRequest('/api/subscription/status', {
            timeoutMs: SUBSCRIPTION_REQUEST_TIMEOUT,
          });
          const verifiedData = {
            ...data,
            owner_uid: currentUser.uid,
            source: 'server',
            verification_failed: false,
          };
          subscriptionInfoRef.current = verifiedData;
          setSubscriptionInfo(verifiedData);
          lastFetchTimeRef.current = now;
          setLastVerifiedAt(now);
          setVerificationFailureReason(null);
          setVerificationState(VERIFICATION_STATE.VERIFIED);
          setLoading(false);
          resetConnectionDiagnostics(true);
          if (attempt > 0) {
            console.info(`✅ [SubscriptionContext] ${attempt + 1}번째 시도에서 성공 | is_pro=${data.is_pro}`);
          }
          // API 성공 → localStorage 캐시 저장
          saveSubscriptionCache(currentUser.uid, verifiedData);
          return verifiedData;
        } catch (err) {
          lastFailureKind = classifyRequestError(err);
          errors.push(err.message);
          const isLastAttempt = attempt >= MAX_RETRIES - 1;
          console.error(
            `❌ [SubscriptionContext] 구독 조회 실패 (${attempt + 1}/${MAX_RETRIES}): ${err.message}`
          );

          if (
            lastFailureKind === ERROR_KIND.NETWORK
            || lastFailureKind === ERROR_KIND.TIMEOUT
          ) {
            startConnectionDiagnostics();
          } else {
            // HTTP 응답을 받았다는 것은 API 호스트 자체에는 도달했다는 뜻이다.
            resetConnectionDiagnostics(true);
          }

          if (!isLastAttempt) {
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
          }
        } finally {
          if (watchdogId) clearTimeout(watchdogId);
        }
      }

      const errorSummary = errors.join(' → ');
      const finalError = errors[errors.length - 1] || '구독 확인 실패';
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const uid = currentUser?.uid || 'unknown';
      const email = currentUser?.email || 'unknown';
      const previousStatus = subscriptionInfoRef.current?.owner_uid === currentUser.uid
        ? subscriptionInfoRef.current
        : null;
      const cached = previousStatus ? null : loadSubscriptionCache(uid);

      setError(finalError);
      setVerificationFailureReason(lastFailureKind);
      setVerificationState(VERIFICATION_STATE.FAILED);
      setLoading(false);

      console.error(
        `🚨 [SUBSCRIPTION_UNKNOWN] 구독 확인 실패, FREE로 단정하지 않음 | uid=${uid} | email=${email} | mobile=${isMobile} | browser=${navigator.userAgent.slice(0, 100)} | errors=[${errorSummary}]`
      );
      try {
        fetch(getApiUrl('/api/log/client-error'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event_type: 'SUBSCRIPTION_UNKNOWN',
            user_uid: uid,
            user_email: email,
            details: errorSummary,
            extra: {
              cached_pro_hint: Boolean(cached && cached.is_pro),
              failure_kind: lastFailureKind,
              last_success_at: lastFetchTimeRef.current ? new Date(lastFetchTimeRef.current).toISOString() : null,
              mobile: isMobile,
              online: navigator.onLine,
              path: window.location.pathname,
              user_agent: navigator.userAgent.slice(0, 200),
            },
          }),
          keepalive: true,
        }).catch(() => {});
      } catch (_e) { /* noop */ }

      if (previousStatus) {
        const staleStatus = {
          ...previousStatus,
          owner_uid: currentUser.uid,
          source: previousStatus.source === 'cache' ? 'cache' : 'memory',
          verification_failed: true,
        };
        console.warn(
          `⚠️ [STALE_SUBSCRIPTION] 이전 상태 임시 유지 | uid=${uid} | is_pro=${Boolean(previousStatus.is_pro)} | errors=[${errorSummary}]`
        );
        subscriptionInfoRef.current = staleStatus;
        setSubscriptionInfo(staleStatus);
        return staleStatus;
      }

      if (cached && cached.is_pro) {
        console.warn(
          `⚠️ [CACHE_PRO_HINT] 서버 확인 실패, 이전 PRO 캐시 존재 | uid=${uid} | email=${email} | mobile=${isMobile} | end_date=${cached.end_date}`
        );
        const cachedProStatus = {
          ...cached,
          owner_uid: currentUser.uid,
          type: cached.type || 'pro',
          status: cached.status || 'active',
          is_pro: true,
          source: 'cache',
          verification_failed: true,
          features: cached.features || SUBSCRIPTION_PLANS.pro.features,
          limitations: cached.limitations || [],
        };
        subscriptionInfoRef.current = cachedProStatus;
        setSubscriptionInfo(cachedProStatus);
        return cachedProStatus;
      }

      const unknownStatus = {
        owner_uid: currentUser.uid,
        type: 'unknown',
        status: 'unknown',
        is_pro: false,
        source: 'unknown',
        verification_failed: true,
        features: [],
        limitations: [],
      };
      subscriptionInfoRef.current = unknownStatus;
      setSubscriptionInfo(unknownStatus);
      return unknownStatus;
    };

    const entry = { uid: currentUser.uid, promise: null };
    entry.promise = runFetch().finally(() => {
      if (inflightRef.current === entry) inflightRef.current = null;
    });
    inflightRef.current = entry;
    return entry.promise;
  }, [
    currentUser,
    makeAuthenticatedRequest,
    resetConnectionDiagnostics,
    startConnectionDiagnostics,
  ]);

  // 구독 플랜 목록 조회
  const fetchSubscriptionPlans = useCallback(async () => {
    try {
      const data = await makeAuthenticatedRequest('/api/subscription/plans');
      if (data.plans) {
        setPlans(data.plans);
      }
    } catch (err) {
      console.error('구독 플랜 조회 실패:', err);
      // 오류 시 기본 플랜 사용
    }
  }, [makeAuthenticatedRequest]);

  // Pro 구독 생성 (기간제)
  const subscribeToPro = useCallback(async (paymentMethod = 'card', planId = 'pro_3month') => {
    setLoading(true);
    setError(null);

    try {
      const data = await makeAuthenticatedRequest('/api/subscription/subscribe', {
        method: 'POST',
        body: JSON.stringify({
          plan_id: planId,
          payment_method: paymentMethod
        })
      });

      if (data.success) {
        // ✅ 기간제 구독 완료 즉시 Pro 상태로 업데이트
        const immediateProStatus = {
          owner_uid: currentUser?.uid,
          type: planId,
          status: 'active',
          is_pro: true,
          features: SUBSCRIPTION_PLANS.pro.features,
          limitations: [],
          start_date: new Date().toISOString(),
          end_date: data.end_date, // 기간제이므로 종료일이 중요
          source: 'server',
          verification_failed: false,
        };
        
        subscriptionInfoRef.current = immediateProStatus;
        setSubscriptionInfo(immediateProStatus);
        setLastVerifiedAt(Date.now());
        setVerificationFailureReason(null);
        setVerificationState(VERIFICATION_STATE.VERIFIED);
        resetConnectionDiagnostics(true);
        
        // 백그라운드에서 정확한 구독 상태 다시 로드 (서버와 동기화)
        setTimeout(async () => {
          try {
            await fetchSubscriptionStatus();
  
          } catch (syncError) {
            console.warn('백그라운드 구독 상태 동기화 실패:', syncError);
          }
        }, 500); // 500ms 후 백그라운드 동기화
        
        return { success: true, message: data.message };
      } else {
        throw new Error(data.error || '구독 생성 실패');
      }
    } catch (err) {
      console.error('Pro 구독 생성 실패:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, fetchSubscriptionStatus, resetConnectionDiagnostics, currentUser?.uid]);

  // 구독 취소
  const cancelSubscription = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await makeAuthenticatedRequest('/api/subscription/cancel', {
        method: 'POST'
      });

      if (data.success) {
        // 구독 상태 새로 고침
        await fetchSubscriptionStatus();
        return { success: true, message: data.message };
      } else {
        throw new Error(data.error || '구독 취소 실패');
      }
    } catch (err) {
      console.error('구독 취소 실패:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, fetchSubscriptionStatus]);

  // Pro 사용자 여부 확인
  const isProUser = useCallback(() => {
    return subscriptionInfo?.is_pro || false;
  }, [subscriptionInfo]);

  // 특정 기능 접근 권한 확인
  const hasFeatureAccess = useCallback((feature) => {
    if (isProUser()) {
      return true; // Pro 사용자는 모든 기능 접근 가능
    }

    // Free 사용자는 기본 기능만 접근 가능
    const freeFeatures = [
      'basic_worksheet_creation',
      'ai_teacher_chat',
      'public_worksheet_access',
      'token_system'
    ];

    return freeFeatures.includes(feature);
  }, [isProUser]);

  // 구독 혜택 정보 가져오기
  const getSubscriptionBenefits = useCallback(() => {
    // is_pro가 false이거나 subscriptionInfo가 없으면 무조건 free 혜택 반환
    if (!subscriptionInfo || !subscriptionInfo.is_pro) {
      return SUBSCRIPTION_PLANS.free;
    }
    
    // is_pro가 true인 경우에만 실제 플랜 타입 확인
    const planType = subscriptionInfo.type || 'free';
    return SUBSCRIPTION_PLANS[planType] || SUBSCRIPTION_PLANS.free;
  }, [subscriptionInfo]);

  // 남은 구독 기간 계산
  const getRemainingDays = useCallback(() => {
    if (!subscriptionInfo?.end_date || subscriptionInfo.type === 'free') {
      return null; // Free 플랜은 무제한
    }

    const endDate = new Date(subscriptionInfo.end_date);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  }, [subscriptionInfo]);

  // 사용자 로그인/로그아웃 시 구독 정보 갱신
  useEffect(() => {
    if (currentUser) {
      fetchSubscriptionStatus();
    } else {
      setSubscriptionInfo(null);
      setError(null);
      setVerificationState(VERIFICATION_STATE.IDLE);
      setVerificationFailureReason(null);
      setLastVerifiedAt(null);
      lastFetchTimeRef.current = null;
      subscriptionInfoRef.current = null;
      resetConnectionDiagnostics(false);
    }
  }, [currentUser, fetchSubscriptionStatus, resetConnectionDiagnostics]);

  useEffect(() => {
    const known = Boolean(currentUser?.uid && subscriptionInfo)
      && subscriptionInfo.source !== 'unknown';
    setTrackedProStatus(currentUser?.uid, known ? subscriptionInfo.is_pro === true : null);
  }, [currentUser, subscriptionInfo]);

  // 구독 취소/환불 시 localStorage 캐시도 즉시 갱신
  useEffect(() => {
    if (currentUser && subscriptionInfo) {
      if (!subscriptionInfo.is_pro || subscriptionInfo.status === 'cancelled' || subscriptionInfo.refunded_at) {
        clearSubscriptionCache(currentUser.uid);
      }
    }
  }, [currentUser, subscriptionInfo]);

  // 탭 복귀 시 구독 상태 자동 갱신 (결제 외부 탭에서 돌아올 때)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentUser) {
        fetchSubscriptionStatus(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [currentUser, fetchSubscriptionStatus]);

  // 친구 초대 보상이 적용되면 5분 캐시를 기다리지 않고 즉시 서버 상태를 반영한다.
  useEffect(() => {
    if (!currentUser) return undefined;
    const handleInviteReward = () => {
      fetchSubscriptionStatus(true);
    };
    window.addEventListener('inviteRewardApplied', handleInviteReward);
    return () => {
      window.removeEventListener('inviteRewardApplied', handleInviteReward);
    };
  }, [currentUser, fetchSubscriptionStatus]);

  // 서버 확인 실패/캐시 PRO 상태는 임시 상태이므로 주기적으로 서버 진실로 복구한다.
  useEffect(() => {
    if (!currentUser) return undefined;

    const needsVerification = subscriptionInfo?.verification_failed || subscriptionInfo?.source === 'cache';
    if (!needsVerification) return undefined;

    const intervalId = setInterval(() => {
      fetchSubscriptionStatus(true);
    }, VERIFICATION_RETRY_INTERVAL);

    return () => clearInterval(intervalId);
  }, [currentUser, subscriptionInfo, fetchSubscriptionStatus]);

  // 학교망/모바일 네트워크 복구 시 리로드 없이 즉시 재검증한다.
  useEffect(() => {
    if (!currentUser) return undefined;

    const handleOnline = () => {
      const latest = subscriptionInfoRef.current;
      if (latest?.verification_failed || latest?.source === 'cache') {
        fetchSubscriptionStatus(true);
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [currentUser, fetchSubscriptionStatus]);

  // 구독 상태 정보를 더 편리하게 접근할 수 있도록 가공
  const subscriptionStatus = subscriptionInfo ? {
    type: subscriptionInfo.type || 'free',
    status: subscriptionInfo.status || 'active',
    isPro: subscriptionInfo.is_pro || false,
    verificationFailed: subscriptionInfo.verification_failed || false,
    startDate: subscriptionInfo.start_date,
    endDate: subscriptionInfo.end_date,
    nextBillingDate: subscriptionInfo.nextBillingDate || subscriptionInfo.end_date,
    lastPaymentKey: subscriptionInfo.last_payment_key,
    refunded_at: subscriptionInfo.refunded_at,
    source: subscriptionInfo.source,
    lastVerifiedAt,
    features: subscriptionInfo.features || [],
    limitations: subscriptionInfo.limitations || []
  } : null;

  const value = {
    // 상태
    subscriptionInfo,
    subscriptionStatus,  // 가공된 구독 상태
    plans,
    loading,
    error,
    connectionState,
    connectionEvidence,
    verificationState,
    verificationFailureReason,
    lastVerifiedAt,
    
    // 메서드
    fetchSubscriptionStatus,
    fetchSubscriptionPlans,
    subscribeToPro,
    cancelSubscription,
    isProUser,
    hasFeatureAccess,
    getSubscriptionBenefits,
    getRemainingDays,
    
    // 유틸리티
    setError,
    setLoading
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext; 